/**
 * Empty state displayed when a player search returns no results.
 *
 * Shared across the player-search surfaces (GlobalSearchModal, AddToListSheet,
 * PlayerSearchResultsList) to keep the "no players found" presentation
 * consistent and DRY.
 * Renders a muted search icon, heading, subtitle, and an optional
 * ManuallyAddPlayer action slot.
 * @module components/shared/search-modal/NoPlayersFound/NoPlayersFoundView
 */

import { SearchX } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Default subtitle copy when no override is supplied */
const DEFAULT_SUBTITLE = "Try a different search or adjust your filters";

/** Default heading copy when no override is supplied */
const DEFAULT_HEADING = "No players found";

/**
 * Where the action slot sits relative to the "no players found" message.
 *
 * `"trailing"` is the shipped order for every search surface where creating a
 * player is a bonus: the reader is told there is no match, and offered the
 * escape hatch underneath it.
 *
 * `"leading"` inverts that for surfaces where the missing player IS the reason
 * the search was opened — quick capture's add-player flow (SE-7242), where a
 * loanee, a trialist or an un-ingested lower-division player is exactly who the
 * scout came to add. Leading with an apology there buries the only useful
 * control on the screen.
 */
export type NoPlayersFoundActionPlacement = "leading" | "trailing";

/**
 * Props for NoPlayersFoundView
 */
interface NoPlayersFoundViewProps {
  /** Optional ManuallyAddPlayer option rendered below the empty message */
  readonly manuallyAddPlayerOption?: React.ReactNode;
  /** Optional subtitle override (e.g. "Try a different name or club.") */
  readonly subtitle?: string;
  /**
   * Optional heading override (e.g. a distinct "we stopped searching" state
   * rather than a confident "no players found" — SE-8324).
   */
  readonly heading?: string;
  /**
   * Where {@link NoPlayersFoundViewProps.manuallyAddPlayerOption} sits.
   * Defaults to `"trailing"`, the order every existing caller renders.
   */
  readonly actionPlacement?: NoPlayersFoundActionPlacement;
}

/**
 * Centered "No players found" empty state with icon and optional ManuallyAddPlayer slot
 * @param props - Component props
 * @param props.manuallyAddPlayerOption - Optional ManuallyAddPlayer option
 * @param props.subtitle - Optional subtitle copy override
 * @param props.heading - Optional heading copy override
 * @param props.actionPlacement - Whether the action sits above or below the message
 */
const NoPlayersFoundView = ({
  manuallyAddPlayerOption,
  subtitle = DEFAULT_SUBTITLE,
  heading = DEFAULT_HEADING,
  actionPlacement = "trailing",
}: NoPlayersFoundViewProps) => (
  <Stack className="items-center gap-4 px-6 pb-6 pt-10">
    {manuallyAddPlayerOption && actionPlacement === "leading" ? (
      <Stack className="items-center">{manuallyAddPlayerOption}</Stack>
    ) : null}
    <Box className="items-center justify-center rounded-full bg-surface-subtle p-4">
      <Icon as={SearchX} size="xl" className="text-content-subtle" />
    </Box>
    <Stack className="items-center gap-1">
      <Text variant="title-sm" className="text-content-primary">
        {heading}
      </Text>
      <Text variant="body" className="text-center text-content-muted">
        {subtitle}
      </Text>
    </Stack>
    {manuallyAddPlayerOption && actionPlacement === "trailing" ? (
      <Stack className="items-center">{manuallyAddPlayerOption}</Stack>
    ) : null}
  </Stack>
);

NoPlayersFoundView.displayName = "NoPlayersFoundView";

export default memo(NoPlayersFoundView);
